import { useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import { Trans } from 'react-i18next';

import { CurrentUserDocument, OrganisationDocument } from 'graphql/types';
import { useSwitchOrganisation } from 'organisation/hooks/useSwitchOrganisation';

const barStyle: SxProps<Theme> = (theme) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar + 1,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    px: 2,
    height: theme.mixins.ghostUserBar.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
});

export const GhostUserBar = () => {
    const { data: userData } = useQuery(CurrentUserDocument);
    const { data: organisationData } = useQuery(OrganisationDocument);
    const { switchOrganisation } = useSwitchOrganisation();

    const sourceOrganisation = userData?.currentUser?.sourceOrganisation;
    const currentOrgName = organisationData?.organisation?.name;

    const handleSwitchBack = useCallback(() => {
        if (!sourceOrganisation) return;

        switchOrganisation(sourceOrganisation.id);
    }, [sourceOrganisation, switchOrganisation]);

    if (!sourceOrganisation) return null;

    return (
        <>
            {/*
             * Set --top-offset so fixed-position elements (AppBar,
             * ContextActionBar, sticky table headers) can offset
             * themselves without querying ghost user state.
             */}
            <GlobalStyles
                styles={(theme) => ({
                    ':root': {
                        '--top-offset': `${theme.mixins.ghostUserBar.height}px`,
                    },
                })}
            />
            <Box sx={barStyle}>
                <Typography variant="body2">
                    <Trans
                        components={{
                            a: (
                                <Link
                                    color="inherit"
                                    sx={{ cursor: 'pointer' }}
                                    underline="always"
                                    variant="body2"
                                    onClick={handleSwitchBack}
                                />
                            ),
                        }}
                        i18nKey="ghostUserBar"
                        values={{ name: currentOrgName ?? '' }}
                    />
                </Typography>
            </Box>
            {/* Spacer to account for the ghost bar height */}
            <Box
                sx={(theme) => ({
                    height: theme.mixins.ghostUserBar.height,
                })}
            />
        </>
    );
};
